<template>
  <nav
    v-if="items.length"
    class="explanation-contents"
    :class="{
      'explanation-contents--top': sticky,
      'explanation-contents--inline': !sticky,
    }"
    aria-label="Article contents"
  >
    <template v-if="sticky">
      <div class="explanation-contents__disclosure">
        <button
          type="button"
          class="explanation-contents__disclosure-button"
          :aria-expanded="contentsExpanded ? 'true' : 'false'"
          @click="toggleContents"
        >
          <span class="explanation-contents__disclosure-text">
            <span class="explanation-contents__label">Contents</span>
            <span class="explanation-contents__crumb">
              {{ activeLabel }}
            </span>
          </span>
          <v-icon size="20">
            {{ contentsExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
          </v-icon>
        </button>

        <div
          v-show="contentsExpanded"
          class="explanation-contents__disclosure-panel"
        >
          <ExplanationContentsList
            :items="items"
            :active-id="activeId"
            @select="handleSelect"
          />
        </div>
      </div>
    </template>

    <div v-else class="explanation-contents__inline-panel">
      <div class="explanation-contents__title">Contents</div>
      <ExplanationContentsList
        :items="items"
        :active-id="activeId"
        @select="handleSelect"
      />
    </div>
  </nav>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import ExplanationContentsList from "@/components/ExplanationContentsList.vue";

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  activeId: {
    type: String,
    default: null,
  },
  sticky: {
    type: Boolean,
    default: true,
  },
});

const contentsExpanded = ref(false);
const userToggledContents = ref(false);

const activeItem = computed(
  () => props.items.find((item) => item.id === props.activeId) || null,
);

const activeLabel = computed(() => activeItem.value?.text || "Contents");

const defaultContentsExpanded = computed(() => props.items.length <= 4);

watch(
  defaultContentsExpanded,
  (value) => {
    if (!userToggledContents.value) contentsExpanded.value = value;
  },
  { immediate: true },
);

watch(
  () => props.items.map((item) => item.id).join("|"),
  () => {
    userToggledContents.value = false;
    contentsExpanded.value = defaultContentsExpanded.value;
  },
);

const toggleContents = () => {
  userToggledContents.value = true;
  contentsExpanded.value = !contentsExpanded.value;
};

const handleSelect = async (id) => {
  contentsExpanded.value = false;
  await nextTick();

  const target =
    typeof document !== "undefined" ? document.getElementById(id) : null;
  if (!target) return;

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  target.scrollIntoView({
    behavior: prefersReducedMotion ? "auto" : "smooth",
    block: "start",
  });

  if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
  target.focus({ preventScroll: true });

  if (typeof window !== "undefined" && window.history?.replaceState) {
    window.history.replaceState(null, "", `#${id}`);
  }
};
</script>

<style lang="scss" scoped>
.explanation-contents {
  width: 100%;
  color: rgb(var(--v-theme-on-surface));
}

.explanation-contents--top {
  margin: 0.75rem 0 1rem;
}

.explanation-contents--inline {
  max-width: 360px;
  margin: 0.75rem 0 1rem;
}

.explanation-contents__disclosure,
.explanation-contents__inline-panel {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.14);
  border-radius: 8px;
  background: rgb(var(--v-theme-surface));
}

.explanation-contents__disclosure {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
}

.explanation-contents__disclosure-button {
  display: flex;
  width: 100%;
  min-height: 46px;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.55rem 0.75rem;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.explanation-contents__disclosure-text {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.1rem;
}

.explanation-contents__label {
  color: rgba(var(--v-theme-on-surface), 0.62);
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1;
}

.explanation-contents__crumb {
  overflow: hidden;
  max-width: 100%;
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.explanation-contents__disclosure-panel,
.explanation-contents__inline-panel {
  padding: 0.5rem;
}

.explanation-contents__disclosure-panel {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.1);
}

.explanation-contents__title {
  margin: 0 0 0.35rem;
  padding: 0 0.5rem;
  color: rgba(var(--v-theme-on-surface), 0.72);
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.4;
}

@media print {
  .explanation-contents {
    max-width: none;
  }

  .explanation-contents__disclosure {
    box-shadow: none;
  }
}
</style>
